import React, { useState, useEffect } from 'react'
import { Table, Tag, Progress, Tooltip, Card } from 'antd'
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import { getSplitPayments } from '../../backend-integration/api/paymentAllocation'

const SplitPaymentsList = ({ refreshKey }) => {
  const [loading, setLoading] = useState(false)
  const [payments, setPayments] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })

  useEffect(() => {
    fetchPayments()
  }, [pagination.current, pagination.pageSize, refreshKey])

  const fetchPayments = async () => {
    setLoading(true)
    try {
      const { data, total } = await getSplitPayments({
        page: pagination.current,
        limit: pagination.pageSize
      })
      setPayments(data)
      setPagination((prev) => ({ ...prev, total }))
    } catch (error) {
      console.error('Error fetching split payments:', error)
    } finally {
      setLoading(false)
    }
  }

  const getAllocationProgress = (payment) => {
    const allocated = payment.allocations.reduce((sum, alloc) => sum + alloc.amount, 0)
    const percentage = (allocated / payment.totalAmount) * 100
    return {
      allocated,
      percentage,
      remaining: payment.totalAmount - allocated
    }
  }

  const columns = [
    {
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      key: 'paymentDate',
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text) => <a>{text}</a>
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => `R ${amount.toFixed(2)}`,
      align: 'right'
    },
    {
      title: 'Allocation Progress',
      key: 'progress',
      render: (_, record) => {
        const { allocated, percentage, remaining } = getAllocationProgress(record)
        return (
          <Tooltip
            title={
              <div>
                <div>Allocated: R{allocated.toFixed(2)}</div>
                <div>Remaining: R{remaining.toFixed(2)}</div>
              </div>
            }
          >
            <Progress
              percent={Math.round(percentage)}
              size='small'
              status={percentage === 100 ? 'success' : 'active'}
            />
          </Tooltip>
        )
      }
    },
    {
      title: 'Invoices',
      dataIndex: 'allocations',
      key: 'allocations',
      render: (allocations) => (
        <div style={{ maxWidth: 300 }}>
          {allocations.map((alloc, index) => (
            <Tag
              key={index}
              icon={alloc.status === 'allocated' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              color={alloc.status === 'allocated' ? 'success' : 'processing'}
            >
              {alloc.invoiceNo}: R{alloc.amount.toFixed(2)}
            </Tag>
          ))}
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const { percentage } = getAllocationProgress(record)
        if (percentage === 100) {
          return <Tag color='success'>Fully Allocated</Tag>
        } else if (percentage > 0) {
          return <Tag color='processing'>Partially Allocated</Tag>
        }
        return <Tag color='default'>Pending</Tag>
      }
    }
  ]

  const expandedRowRender = (record) => {
    const allocationColumns = [
      {
        title: 'Invoice #',
        dataIndex: 'invoiceNo',
        key: 'invoiceNo'
      },
      {
        title: 'Patient',
        dataIndex: 'patientName',
        key: 'patientName'
      },
      {
        title: 'Allocated Amount',
        dataIndex: 'amount',
        key: 'amount',
        render: (amount) => `R ${amount.toFixed(2)}`,
        align: 'right'
      },
      {
        title: 'Allocation Date',
        dataIndex: 'allocationDate',
        key: 'allocationDate',
        render: (date) => moment(date).format('YYYY-MM-DD HH:mm')
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => <Tag color={status === 'allocated' ? 'success' : 'processing'}>{status}</Tag>
      }
    ]

    return (
      <Card size='small' title='Allocation Details'>
        <Table
          columns={allocationColumns}
          dataSource={record.allocations}
          rowKey={(row, index) => `${record._id}-${index}`}
          pagination={false}
          size='small'
        />
      </Card>
    )
  }

  return (
    <Table
      columns={columns}
      dataSource={payments}
      rowKey='_id'
      loading={loading}
      expandable={{
        expandedRowRender,
        rowExpandable: (record) => record.allocations && record.allocations.length > 0
      }}
      pagination={{
        ...pagination,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} split payments`,
        onChange: (page, pageSize) => {
          setPagination((prev) => ({
            ...prev,
            current: page,
            pageSize
          }))
        }
      }}
    />
  )
}

export default SplitPaymentsList
